import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toolsApi, transactionsApi, ideasApi, journalApi, backfillApi, backfillQuarterRanges, replayApi, runJournalBackfillUntilDone, type ReplayProgressEvent } from '@/lib/api/client';
import type { IdeaIssueRow } from '@steward/shared';
import { useLinkAllCandidates } from '@/hooks/queries/useCandidates';
import { cn } from '@/lib/utils';

type ToolResult = {
  status: 'idle' | 'running' | 'success' | 'error';
  message?: string;
};

/** A single quarter's `{ start, end }` range (ISO strings — see
 * backfillQuarterRanges) paired with why it failed. */
export interface BackfillQuarterFailure {
  start: string;
  end: string;
  message: string;
}

export interface BackfillTransactionsCardProps {
  status: 'idle' | 'running' | 'done';
  /** 1-based index of the quarter currently running, or the last one run
   * once `status` is 'done'. 0 while idle. */
  currentQuarter: number;
  totalQuarters: number;
  inserted: number;
  skipped: number;
  failedQuarters: BackfillQuarterFailure[];
  onRun: () => void;
}

/**
 * Presentational — owns no fetch, no state. `ToolsPage` drives the
 * quarter-by-quarter loop (one POST per quarter, sequential, never
 * parallel — see the Task 8 brief) and passes down progress as it happens,
 * following this codebase's convention of testing logic-bearing UI as
 * props-driven components rather than by seeding a query cache.
 */
export function BackfillTransactionsCard({
  status,
  currentQuarter,
  totalQuarters,
  inserted,
  skipped,
  failedQuarters,
  onRun,
}: BackfillTransactionsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Backfill Schwab transactions</CardTitle>
        <CardDescription>
          Pulls the full API history (2024-03-25 onward) as a second, higher-fidelity source.
          Existing rows are never modified — this only adds. Safe to re-run; a completed range
          writes nothing.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Button onClick={onRun} disabled={status === 'running'}>
              {status === 'running' ? 'Running…' : 'Run Backfill'}
            </Button>
            {status === 'running' && totalQuarters > 0 && (
              <span className="text-sm text-gray-500" data-testid="backfill-progress">
                Quarter {currentQuarter} of {totalQuarters} — {inserted} inserted, {skipped} skipped so far
              </span>
            )}
            {status === 'done' && (
              <span className="text-sm text-green-600" data-testid="backfill-summary">
                Backfill complete: {inserted} inserted, {skipped} skipped across {totalQuarters} quarter{totalQuarters === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {failedQuarters.length > 0 && (
            <div className="text-sm text-red-600" data-testid="backfill-failures">
              <p className="font-medium">
                {failedQuarters.length} quarter{failedQuarters.length === 1 ? '' : 's'} failed:
              </p>
              <ul className="list-disc list-inside">
                {failedQuarters.map((f, i) => (
                  <li key={i}>
                    {f.start.slice(0, 10)} to {f.end.slice(0, 10)}: {f.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Flattened `ReplayResult` counts, as the terminal SSE event carries them. */
export interface ReplayResultSummary {
  transactionsRead: number;
  ordersDerived: number;
  stockOrdersDerived: number;
  chainsCreated: number;
  positionsCreated: number;
  positionsClosed: number;
}

export interface ReplayCardProps {
  status: 'idle' | 'running' | 'done' | 'error';
  /** Progress counters from intervening `'processing'` events. Meaningful only while running. */
  current: number;
  total: number;
  result: ReplayResultSummary | null;
  error: string | null;
  /**
   * Frugal-mode follow-up chain status (Task 7b): `/import/replay` runs with `updatePnl: false,
   * closeExpired: false, capture: false` to stay under D1's ~1,000-statement budget, so the
   * driver chains fix-chain-statuses -> recalculate-P&L -> journal-backfill -> rebuild-stock
   * (Task 16) afterward, the same way `JsonImport.tsx` already does. Empty/undefined hides the
   * line — same convention as `error`/`result`.
   */
  followUpMessage?: string;
  onRun: () => void;
}

/**
 * Presentational — owns no fetch, no state, same convention as `BackfillTransactionsCard`
 * above. `ToolsPage` drives the single SSE POST to `/import/replay` and passes down progress
 * as it streams.
 */
export function ReplayCard({ status, current, total, result, error, followUpMessage, onRun }: ReplayCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Replay from transactions</CardTitle>
        <CardDescription>
          Rebuilds every trade chain, position and leg from the stored transactions — the source
          of truth. Existing derived data is cleared and rebuilt from scratch; transactions and
          your journal entries are never touched.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Button onClick={onRun} disabled={status === 'running'}>
              {status === 'running' ? 'Replaying…' : 'Replay from transactions'}
            </Button>
            {status === 'running' && (
              <span className="text-sm text-gray-500" data-testid="replay-progress">
                Processing {current} of {total}…
              </span>
            )}
            {status === 'done' && result && (
              <span className="text-sm text-green-600" data-testid="replay-summary">
                Replay complete: {result.transactionsRead} transactions read, {result.ordersDerived} orders derived
                ({result.chainsCreated} chains, {result.positionsCreated} positions created, {result.positionsClosed} closed)
              </span>
            )}
          </div>
          {status === 'error' && error && (
            <div className="text-sm text-red-600" data-testid="replay-error">
              {error}
            </div>
          )}
          {followUpMessage && (
            <div className="text-sm text-gray-500" data-testid="replay-followup">
              {followUpMessage}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export interface FixChainStatusesResult {
  expiredClosed: number;
  positionsFixed: number;
  stockPositionsClosed: number;
  chainsRecalculated: number;
}

export interface FixChainStatusesWithJournalDeps {
  fixChainStatuses: () => Promise<FixChainStatusesResult>;
  backfillJournal: () => Promise<unknown>;
}

export interface FixChainStatusesWithJournalResult extends FixChainStatusesResult {
  /** False when the journal backfill step itself failed — the sweep above still succeeded. */
  journalUpdated: boolean;
}

/**
 * Drives the standalone "Fix Chain Statuses" button (Task 19 review fix).
 *
 * The extraction that moved this route's core into `sweepChainStatuses`
 * (`services/order-pipeline/chain-status-sweep.ts`) deliberately dropped the route's inline
 * `captureAfterPipeline` call — see that file's doc comment — because the cron `sweep` job and
 * the replay/import follow-up chains (`runReplayWithFollowUps`/`runImportFollowUps` below) both
 * already run a full journal backfill afterward. This standalone button is the one caller with NO
 * other follow-up: clicked alone, it used to capture journal events for the positions it just
 * closed as a side effect of the route itself; after the extraction it silently stopped doing
 * that, recoverable only via a separate manual "Build journal history" click. This driver restores
 * that behavior on the WEB side (the route itself stays unchanged, per the review): after a
 * successful `fixChainStatuses`, it runs the same `runJournalBackfillUntilDone` loop the
 * replay/import chains use.
 *
 * The journal step is independently try/caught (mirrors every follow-up in
 * `runReplayWithFollowUps`/`runImportFollowUps`) — a failed backfill must not mask that the sweep
 * itself succeeded, so it's reported via `journalUpdated: false` rather than thrown. `fixChainStatuses`
 * itself is NOT caught here: a throw from it propagates to the caller unchanged, and the journal
 * step is never reached — closing positions is the load-bearing half of this action, and there is
 * nothing to journal if it never ran.
 */
export async function runFixChainStatusesWithJournal(
  deps: FixChainStatusesWithJournalDeps,
): Promise<FixChainStatusesWithJournalResult> {
  const data = await deps.fixChainStatuses();

  let journalUpdated = false;
  try {
    await deps.backfillJournal();
    journalUpdated = true;
  } catch (err) {
    console.warn('[fix-chain-statuses] journal backfill failed:', err);
  }

  return { ...data, journalUpdated };
}

/**
 * Drives the full replay round-trip for the "Replay from transactions" card. `/import/replay`
 * (Task 7b) runs frugal (`updatePnl: false, closeExpired: false, capture: false` server-side) to
 * stay under D1's ~1,000-statement Worker budget, deferring P&L recalculation, expiry-closing and
 * journal capture to follow-up invocations — the same diet the JSON import path
 * (`JsonImport.tsx`) already uses. This chains those follow-ups after a successful replay:
 * fix-chain-statuses -> recalculate-P&L -> journal-history backfill -> rebuild-stock (Task 16),
 * in that order (mirrors `handleJournalBackfill`'s own client call). An `'error'` terminal event
 * from `run` skips the follow-up chain entirely — a replay that didn't finish successfully must
 * not be treated as though its derived graph is trustworthy enough to recalculate P&L against.
 *
 * Each follow-up call is independently try/caught and warned on failure (mirrors
 * `JsonImport.tsx:152-164`'s `handleBatchedImport`) so one follow-up failing doesn't stop the
 * next or mask that the replay itself succeeded.
 *
 * `rebuildStock` (Task 16) runs after the journal backfill: `/import/rebuild-stock-positions`
 * is its own invocation deliberately, not folded into the replay itself — replay is budget-measured
 * at 537 statements and the rebuild is a full chronological pass over every stock-shaped
 * transaction, so keeping it separate mirrors how the old workflow always ran it by hand after
 * import/replay rather than inline.
 *
 * `reconcileStock` (Task 17) runs LAST, after the stock rebuild: `/import/reconcile-stock-positions`
 * force-matches against Schwab's live holdings, catching corporate actions absent from
 * `transactions` that a rebuild-from-transactions cannot see (measured: NAKD/FCEL phantoms). Also
 * its own invocation and independently try/caught, same reasoning as the others.
 *
 * Pure aside from the injected `run`/follow-up functions — testable without a fetch mock, same
 * convention as `ReplayCard`/`BackfillTransactionsCard` above being props-driven rather than
 * fetch-driven.
 */
export interface ReplayWithFollowUpsDeps {
  run: (onProgress: (event: ReplayProgressEvent) => void) => Promise<ReplayProgressEvent>;
  onProgress: (event: ReplayProgressEvent) => void;
  fixChainStatuses: () => Promise<unknown>;
  recalculatePnl: () => Promise<unknown>;
  backfillJournal: () => Promise<unknown>;
  rebuildStock: () => Promise<unknown>;
  reconcileStock: () => Promise<unknown>;
  onFollowUpMessage?: (message: string) => void;
}

export async function runReplayWithFollowUps(deps: ReplayWithFollowUpsDeps): Promise<ReplayProgressEvent> {
  const final = await deps.run(deps.onProgress);
  if (final.phase === 'error') {
    return final;
  }

  deps.onFollowUpMessage?.('Finalizing: fixing chain statuses…');
  try {
    await deps.fixChainStatuses();
  } catch (err) {
    console.warn('[replay] fix-chain-statuses failed:', err);
  }

  deps.onFollowUpMessage?.('Finalizing: recalculating P&L…');
  try {
    await deps.recalculatePnl();
  } catch (err) {
    console.warn('[replay] recalculate-pnl failed:', err);
  }

  deps.onFollowUpMessage?.('Finalizing: rebuilding journal history…');
  try {
    await deps.backfillJournal();
  } catch (err) {
    console.warn('[replay] journal backfill failed:', err);
  }

  deps.onFollowUpMessage?.('Finalizing: rebuilding stock positions…');
  try {
    await deps.rebuildStock();
  } catch (err) {
    console.warn('[replay] rebuild-stock-positions failed:', err);
  }

  deps.onFollowUpMessage?.('Finalizing: reconciling with Schwab…');
  try {
    await deps.reconcileStock();
  } catch (err) {
    console.warn('[replay] reconcile-stock-positions failed:', err);
  }

  deps.onFollowUpMessage?.('');
  return final;
}

export function ToolsPage() {
  const [fixStatusResult, setFixStatusResult] = useState<ToolResult>({ status: 'idle' });
  const [recalcResult, setRecalcResult] = useState<ToolResult>({ status: 'idle' });
  const [journalBackfillResult, setJournalBackfillResult] = useState<ToolResult>({ status: 'idle' });
  const [stockRebuildResult, setStockRebuildResult] = useState<ToolResult>({ status: 'idle' });
  const linkCandidates = useLinkAllCandidates();
  const [schwabReconcileResult, setSchwabReconcileResult] = useState<ToolResult>({ status: 'idle' });
  const [reconcileResult, setReconcileResult] = useState<{
    status: 'idle' | 'running' | 'success' | 'error';
    message?: string;
    data?: Awaited<ReturnType<typeof toolsApi.reconcile>>;
  }>({ status: 'idle' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txnFileInputRef = useRef<HTMLInputElement>(null);
  const [txnReconcileResult, setTxnReconcileResult] = useState<{
    status: 'idle' | 'running' | 'success' | 'error';
    message?: string;
    data?: Awaited<ReturnType<typeof transactionsApi.reconcile>>;
  }>({ status: 'idle' });
  const [resetResult, setResetResult] = useState<ToolResult>({ status: 'idle' });
  const [confirmReset, setConfirmReset] = useState(false);
  const [issues, setIssues] = useState<{ status: 'idle' | 'loading' | 'error'; data?: IdeaIssueRow[]; message?: string }>({ status: 'idle' });
  const [backfillState, setBackfillState] = useState<{
    status: 'idle' | 'running' | 'done';
    currentQuarter: number;
    totalQuarters: number;
    inserted: number;
    skipped: number;
    failedQuarters: BackfillQuarterFailure[];
  }>({ status: 'idle', currentQuarter: 0, totalQuarters: 0, inserted: 0, skipped: 0, failedQuarters: [] });
  const [replayState, setReplayState] = useState<{
    status: 'idle' | 'running' | 'done' | 'error';
    current: number;
    total: number;
    result: ReplayResultSummary | null;
    error: string | null;
  }>({ status: 'idle', current: 0, total: 0, result: null, error: null });
  const [replayFollowUpMessage, setReplayFollowUpMessage] = useState('');

  async function loadIssues() {
    setIssues({ status: 'loading' });
    try {
      const res = await ideasApi.listIssues();
      setIssues({ status: 'idle', data: res.issues });
    } catch (err) {
      setIssues({ status: 'error', message: err instanceof Error ? err.message : 'Failed' });
    }
  }

  async function handleRecalculatePnl() {
    setRecalcResult({ status: 'running' });
    try {
      const data = await toolsApi.recalculatePnl();
      setRecalcResult({
        status: 'success',
        message: `Updated ${data.updated} of ${data.total} chains${data.failed ? ` (${data.failed} failed)` : ''}`,
      });
    } catch (err) {
      setRecalcResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
    }
  }

  async function handleJournalBackfill() {
    setJournalBackfillResult({ status: 'running' });
    try {
      // Finding I4: /journal/backfill now processes one bounded batch per call — loop until the
      // server reports done, the same client-loop shape BackfillTransactionsCard's quarter loop
      // already uses.
      const data = await runJournalBackfillUntilDone(journalApi.backfill);
      setJournalBackfillResult({
        status: 'success',
        message: `Events: ${data.eventsInserted} new, ${data.eventsUpdated} corrected, ${data.eventsRemoved} removed; entries: ${data.entriesInserted} new, ${data.entriesUpdated} rewritten, ${data.entriesRemoved} removed; chains: ${data.totalChains}`,
      });
    } catch (err) {
      setJournalBackfillResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
    }
  }

  async function handleResetTradeData() {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }
    setResetResult({ status: 'running' });
    setConfirmReset(false);
    try {
      const data = await toolsApi.resetTradeData();
      const d = data.deleted;
      setResetResult({
        status: 'success',
        message: `Deleted: ${d.tradeChains} chains, ${d.positions} positions, ${d.positionLegs} legs, ${d.legTransactions} leg transactions, ${d.stockPositions || 0} stock positions, cleared transactions & alerts`,
      });
    } catch (err) {
      setResetResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
    }
  }

  async function handleRunBackfill() {
    const quarters = backfillQuarterRanges();
    setBackfillState({
      status: 'running',
      currentQuarter: 0,
      totalQuarters: quarters.length,
      inserted: 0,
      skipped: 0,
      failedQuarters: [],
    });

    let inserted = 0;
    let skipped = 0;
    const failedQuarters: BackfillQuarterFailure[] = [];

    for (let i = 0; i < quarters.length; i++) {
      const range = quarters[i];
      setBackfillState((prev) => ({ ...prev, currentQuarter: i + 1 }));

      // Sequential, never parallel — concurrent writes to the same source
      // would race the per-source dedup. One bad quarter (a transient Schwab
      // error, a shape the adapter cannot parse) is recorded and the loop
      // continues; re-running later is the recovery path since an
      // already-written range is a no-op.
      //
      // Task 9b: the resolved terminal event now tells the truth about this
      // quarter on its own — phase 'error' means it failed, with the reason
      // in `message` — so there is no need to also watch onProgress for an
      // intervening error event to catch what the terminal event used to
      // hide.
      let quarterError: string | null = null;
      try {
        const final = await backfillApi.runSchwabTransactionsQuarter(range, () => {});
        inserted += final.inserted;
        skipped += final.skipped;
        if (final.phase === 'error') {
          quarterError = final.message;
        }
      } catch (err) {
        quarterError = err instanceof Error ? err.message : 'Request failed';
      }

      if (quarterError) {
        failedQuarters.push({ start: range.start, end: range.end, message: quarterError });
      }

      setBackfillState({
        status: 'running',
        currentQuarter: i + 1,
        totalQuarters: quarters.length,
        inserted,
        skipped,
        failedQuarters: [...failedQuarters],
      });
    }

    setBackfillState({
      status: 'done',
      currentQuarter: quarters.length,
      totalQuarters: quarters.length,
      inserted,
      skipped,
      failedQuarters,
    });
  }

  async function handleRunReplay() {
    setReplayState({ status: 'running', current: 0, total: 0, result: null, error: null });
    setReplayFollowUpMessage('');

    try {
      // Frugal-mode follow-up chain (Task 7b) — see runReplayWithFollowUps's doc comment.
      const final = await runReplayWithFollowUps({
        run: replayApi.run,
        onProgress: (event) => {
          if (event.phase === 'processing') {
            setReplayState((prev) => ({ ...prev, current: event.current, total: event.total }));
          }
        },
        fixChainStatuses: toolsApi.fixChainStatuses,
        recalculatePnl: toolsApi.recalculatePnl,
        // Finding I4: journal backfill is now bounded per call — loop until done, same as the
        // "Build journal history" card's own handler above.
        backfillJournal: () => runJournalBackfillUntilDone(journalApi.backfill),
        // Task 16: the battle-tested stock rebuild.
        rebuildStock: toolsApi.rebuildStockPositions,
        // Task 17: reconcile against Schwab's live holdings, run last — see
        // runReplayWithFollowUps's doc comment.
        reconcileStock: toolsApi.reconcileStockPositions,
        onFollowUpMessage: setReplayFollowUpMessage,
      });

      if (final.phase === 'error') {
        setReplayState({
          status: 'error', current: 0, total: 0, result: null,
          error: final.message,
        });
        return;
      }

      setReplayState({
        status: 'done',
        current: final.current,
        total: final.total,
        result: {
          transactionsRead: final.transactionsRead ?? 0,
          ordersDerived: final.ordersDerived ?? 0,
          stockOrdersDerived: final.stockOrdersDerived ?? 0,
          chainsCreated: final.chainsCreated ?? 0,
          positionsCreated: final.positionsCreated ?? 0,
          positionsClosed: final.positionsClosed ?? 0,
        },
        error: null,
      });
    } catch (err) {
      setReplayState({
        status: 'error', current: 0, total: 0, result: null,
        error: err instanceof Error ? err.message : 'Request failed',
      });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Tools</h1>
        <p className="text-sm text-gray-500 mt-1">
          Maintenance and troubleshooting utilities
        </p>
      </div>

      {/* Fix Chain Statuses */}
      <Card>
        <CardHeader>
          <CardTitle>Fix Chain Statuses</CardTitle>
          <CardDescription>
            Closes positions where all legs are fully closed but the position status is still open.
            Also closes orphaned stock positions where all option positions on the chain are closed.
            Then recalculates P&L for affected chains.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={async () => {
                setFixStatusResult({ status: 'running' });
                try {
                  const data = await runFixChainStatusesWithJournal({
                    fixChainStatuses: toolsApi.fixChainStatuses,
                    backfillJournal: () => runJournalBackfillUntilDone(journalApi.backfill),
                  });
                  setFixStatusResult({
                    status: 'success',
                    message: `Expired: ${data.expiredClosed}, fixed ${data.positionsFixed} positions, closed ${data.stockPositionsClosed} stock positions, recalculated ${data.chainsRecalculated} chains · journal ${data.journalUpdated ? 'updated' : 'update failed'}`,
                  });
                } catch (err) {
                  setFixStatusResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
                }
              }}
              disabled={fixStatusResult.status === 'running'}
            >
              {fixStatusResult.status === 'running' ? 'Fixing...' : 'Fix Chain Statuses'}
            </Button>
            {fixStatusResult.status === 'success' && (
              <span className="text-sm text-green-600">{fixStatusResult.message}</span>
            )}
            {fixStatusResult.status === 'error' && (
              <span className="text-sm text-red-600">{fixStatusResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recalculate P&L */}
      <Card>
        <CardHeader>
          <CardTitle>Recalculate P&L</CardTitle>
          <CardDescription>
            Recalculate realized P&L for all trade chains. Use this after fixing data issues
            or if P&L values appear incorrect (e.g., showing $0 for expired positions).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleRecalculatePnl}
              disabled={recalcResult.status === 'running'}
            >
              {recalcResult.status === 'running' ? 'Recalculating...' : 'Recalculate All P&L'}
            </Button>
            {recalcResult.status === 'success' && (
              <span className="text-sm text-green-600">{recalcResult.message}</span>
            )}
            {recalcResult.status === 'error' && (
              <span className="text-sm text-red-600">{recalcResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Build Journal History */}
      <Card>
        <CardHeader>
          <CardTitle>Build journal history</CardTitle>
          <CardDescription>
            Derives open/roll/expire/close events for every chain and writes facts-only journal
            entries. Safe to re-run — existing events are skipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleJournalBackfill}
              disabled={journalBackfillResult.status === 'running'}
            >
              {journalBackfillResult.status === 'running' ? 'Building...' : 'Build journal history'}
            </Button>
            {journalBackfillResult.status === 'success' && (
              <span className="text-sm text-green-600">{journalBackfillResult.message}</span>
            )}
            {journalBackfillResult.status === 'error' && (
              <span className="text-sm text-red-600">{journalBackfillResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Match bot candidates. Belongs beside the replay card: a replay regenerates every chain
          id, which is exactly what breaks these links and what this repairs. */}
      <Card>
        <CardHeader>
          <CardTitle>Match bot candidates</CardTitle>
          <CardDescription>
            Links every unlinked candidate to an open chain with the same legs, and repairs links
            after a replay. Safe to re-run.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button onClick={() => linkCandidates.mutate()} disabled={linkCandidates.isPending}>
              {linkCandidates.isPending ? 'Matching…' : 'Match bot candidates'}
            </Button>
            {/* The toast says the same thing and then leaves; this line stays on the card, like
                every other action here, so the counts are still readable after it fades. */}
            {linkCandidates.isSuccess && linkCandidates.data && (
              <span className="text-sm text-green-600">
                Linked {linkCandidates.data.linked} · relinked {linkCandidates.data.relinked} ·
                {' '}lost {linkCandidates.data.lost} · errors {linkCandidates.data.errors}
              </span>
            )}
            {linkCandidates.isError && (
              <span className="text-sm text-red-600">{linkCandidates.error?.message || 'Match failed'}</span>
            )}
          </div>
        </CardContent>
      </Card>

      <BackfillTransactionsCard
        status={backfillState.status}
        currentQuarter={backfillState.currentQuarter}
        totalQuarters={backfillState.totalQuarters}
        inserted={backfillState.inserted}
        skipped={backfillState.skipped}
        failedQuarters={backfillState.failedQuarters}
        onRun={() => void handleRunBackfill()}
      />

      <ReplayCard
        status={replayState.status}
        current={replayState.current}
        total={replayState.total}
        result={replayState.result}
        error={replayState.error}
        followUpMessage={replayFollowUpMessage}
        onRun={() => void handleRunReplay()}
      />

      {/* Rebuild Stock Positions */}
      <Card>
        <CardHeader>
          <CardTitle>Rebuild Stock Positions</CardTitle>
          <CardDescription>
            Rebuild stock position records from existing assignment/exercise transactions.
            Fixes P&L calculations for chains with stock buys/sells from options assignments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={async () => {
                setStockRebuildResult({ status: 'running' });
                try {
                  const data = await toolsApi.rebuildStockPositions();
                  setStockRebuildResult({
                    status: 'success',
                    message: `Created ${data.stockPositionsCreated} stock positions (${data.stockPositionsOpen} open, ${data.stockPositionsClosed} closed), recalculated ${data.chainsRecalculated} chains`,
                  });
                } catch (err) {
                  setStockRebuildResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
                }
              }}
              disabled={stockRebuildResult.status === 'running'}
            >
              {stockRebuildResult.status === 'running' ? 'Rebuilding...' : 'Rebuild Stock Positions'}
            </Button>
            {stockRebuildResult.status === 'success' && (
              <span className="text-sm text-green-600">{stockRebuildResult.message}</span>
            )}
            {stockRebuildResult.status === 'error' && (
              <span className="text-sm text-red-600">{stockRebuildResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reconcile Stock Positions with Schwab */}
      <Card>
        <CardHeader>
          <CardTitle>Reconcile Stock Positions</CardTitle>
          <CardDescription>
            Fetch current equity positions from Schwab and correct Steward's stock positions
            to match. Fixes discrepancies from missing transactions, splits, mergers, and delistings.
            Run after Rebuild Stock Positions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={async () => {
                setSchwabReconcileResult({ status: 'running' });
                try {
                  const data = await toolsApi.reconcileStockPositions();
                  setSchwabReconcileResult({
                    status: 'success',
                    message: data.changes.length > 0
                      ? `${data.changes.length} changes: ${data.changes.join('; ')}`
                      : `All ${data.schwabEquityCount} positions match Schwab`,
                  });
                } catch (err) {
                  setSchwabReconcileResult({ status: 'error', message: err instanceof Error ? err.message : 'Request failed' });
                }
              }}
              disabled={schwabReconcileResult.status === 'running'}
            >
              {schwabReconcileResult.status === 'running' ? 'Reconciling...' : 'Reconcile with Schwab'}
            </Button>
            {schwabReconcileResult.status === 'success' && (
              <span className="text-sm text-green-600">{schwabReconcileResult.message}</span>
            )}
            {schwabReconcileResult.status === 'error' && (
              <span className="text-sm text-red-600">{schwabReconcileResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reconcile with Schwab */}
      <Card>
        <CardHeader>
          <CardTitle>Reconcile with Schwab</CardTitle>
          <CardDescription>
            Upload a Schwab Realized Gain/Loss CSV export to compare against Steward's P&L data.
            Go to Schwab &gt; Accounts &gt; History &gt; Realized Gains &amp; Losses &gt; Export.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
              />
              <Button
                onClick={async () => {
                  const file = fileInputRef.current?.files?.[0];
                  if (!file) return;
                  setReconcileResult({ status: 'running' });
                  try {
                    const data = await toolsApi.reconcile(file);
                    setReconcileResult({ status: 'success', data });
                  } catch (err) {
                    setReconcileResult({ status: 'error', message: err instanceof Error ? err.message : 'Failed' });
                  }
                }}
                disabled={reconcileResult.status === 'running'}
              >
                {reconcileResult.status === 'running' ? 'Comparing...' : 'Upload & Compare'}
              </Button>
            </div>
            {reconcileResult.status === 'error' && (
              <span className="text-sm text-red-600">{reconcileResult.message}</span>
            )}
            {reconcileResult.status === 'success' && reconcileResult.data && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">Schwab Total</div>
                    <div className={`font-semibold ${reconcileResult.data.summary.schwabTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${reconcileResult.data.summary.schwabTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">Steward Total</div>
                    <div className={`font-semibold ${reconcileResult.data.summary.stewardTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${reconcileResult.data.summary.stewardTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">Difference</div>
                    <div className={`font-semibold ${Math.abs(reconcileResult.data.summary.difference) <= 10 ? 'text-green-600' : 'text-red-600'}`}>
                      ${reconcileResult.data.summary.difference.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {reconcileResult.data.summary.symbolsMatched} matched, {reconcileResult.data.summary.symbolsMismatched} mismatched
                  {reconcileResult.data.summary.symbolsOnlyInSchwab > 0 && `, ${reconcileResult.data.summary.symbolsOnlyInSchwab} only in Schwab`}
                  {reconcileResult.data.summary.symbolsOnlyInSteward > 0 && `, ${reconcileResult.data.summary.symbolsOnlyInSteward} only in Steward`}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-gray-500">
                        <th className="py-2 pr-4">Symbol</th>
                        <th className="py-2 pr-4 text-right">Schwab</th>
                        <th className="py-2 pr-4 text-right">Steward</th>
                        <th className="py-2 pr-4 text-right">Diff</th>
                        <th className="py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconcileResult.data.symbols.map((row) => (
                        <tr key={row.symbol} className="border-b border-gray-50">
                          <td className="py-1.5 pr-4 font-medium">{row.symbol}</td>
                          <td className="py-1.5 pr-4 text-right tabular-nums">${row.schwabPnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-1.5 pr-4 text-right tabular-nums">${row.stewardPnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className={`py-1.5 pr-4 text-right tabular-nums ${Math.abs(row.difference) <= 10 ? 'text-gray-400' : 'text-red-600 font-medium'}`}>
                            ${row.difference.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                              row.status === 'match' ? 'bg-green-100 text-green-700' :
                              row.status === 'mismatch' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {row.status === 'match' ? 'OK' : row.status === 'mismatch' ? 'DIFF' : row.status === 'schwab_only' ? 'Schwab only' : 'Steward only'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reconcile Transactions (JSON) */}
      <Card>
        <CardHeader>
          <CardTitle>Reconcile Transactions</CardTitle>
          <CardDescription>
            Upload a Schwab JSON transaction export to verify it matches stored transactions.
            This checks the uploaded file against your transaction history — matched transactions
            confirm your data is complete. Works for all history (not limited to 2024+).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                ref={txnFileInputRef}
                type="file"
                accept=".json"
                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
              />
              <Button
                onClick={async () => {
                  const file = txnFileInputRef.current?.files?.[0];
                  if (!file) return;
                  setTxnReconcileResult({ status: 'running' });
                  try {
                    const text = await file.text();
                    const jsonData = JSON.parse(text);
                    const data = await transactionsApi.reconcile(jsonData);
                    setTxnReconcileResult({ status: 'success', data });
                  } catch (err) {
                    setTxnReconcileResult({ status: 'error', message: err instanceof Error ? err.message : 'Failed' });
                  }
                }}
                disabled={txnReconcileResult.status === 'running'}
              >
                {txnReconcileResult.status === 'running' ? 'Comparing...' : 'Upload & Compare'}
              </Button>
            </div>
            {txnReconcileResult.status === 'error' && (
              <span className="text-sm text-red-600">{txnReconcileResult.message}</span>
            )}
            {txnReconcileResult.status === 'success' && txnReconcileResult.data && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">Uploaded</div>
                    <div className="font-semibold">{txnReconcileResult.data.summary.uploaded}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-gray-500">Matched</div>
                    <div className="font-semibold text-green-600">{txnReconcileResult.data.summary.matched}</div>
                  </div>
                  <div className={cn('rounded-lg p-3', txnReconcileResult.data.summary.missing > 0 ? 'bg-red-50' : 'bg-gray-50')}>
                    <div className="text-gray-500">Missing</div>
                    <div className={cn('font-semibold', txnReconcileResult.data.summary.missing > 0 ? 'text-red-600' : '')}>
                      {txnReconcileResult.data.summary.missing}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">Match Rate</div>
                    <div className={cn('font-semibold', txnReconcileResult.data.summary.matchPercent >= 95 ? 'text-green-600' : 'text-amber-600')}>
                      {txnReconcileResult.data.summary.matchPercent}%
                    </div>
                  </div>
                </div>

                {txnReconcileResult.data.summary.missing > 0 && (
                  <div className="overflow-x-auto">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Missing from Steward:</h3>
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500">
                          <th className="py-2 pr-4">Date</th>
                          <th className="py-2 pr-4">Type</th>
                          <th className="py-2 pr-4">Symbol</th>
                          <th className="py-2 pr-4">Action</th>
                          <th className="py-2 pr-4 text-right">Qty</th>
                          <th className="py-2 pr-4 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {txnReconcileResult.data.results
                          .filter(r => r.status === 'missing')
                          .map((row, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="py-1.5 pr-4 tabular-nums">
                                {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                              </td>
                              <td className="py-1.5 pr-4">
                                <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">
                                  {row.transactionType.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="py-1.5 pr-4 font-medium">{row.symbol}</td>
                              <td className="py-1.5 pr-4 text-gray-600">{row.action}</td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">{row.quantity || '-'}</td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">
                                {row.amount ? `$${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {txnReconcileResult.data.summary.missing === 0 && (
                  <div className="text-sm text-green-600 font-medium">
                    All {txnReconcileResult.data.summary.uploaded} transactions in the upload are stored in Steward.
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Idea Data-Quality Reports */}
      <Card>
        <CardHeader>
          <CardTitle>Idea Data-Quality Reports</CardTitle>
          <CardDescription>
            "Something's off" reports submitted from the Trade Ideas page. Includes a snapshot of the
            suggestion and, when Tradier is configured, a cross-source price comparison.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Button onClick={() => void loadIssues()} disabled={issues.status === 'loading'}>
              {issues.status === 'loading' ? 'Loading…' : 'Load reports'}
            </Button>
            {issues.status === 'error' && <span className="text-sm text-red-600">{issues.message}</span>}
            {issues.data && issues.data.length === 0 && <div className="text-sm text-gray-500">No reports yet.</div>}
            {issues.data && issues.data.length > 0 && (
              <div className="space-y-2">
                {issues.data.map((it) => (
                  <div key={it.id} className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{it.symbol}</span>
                      <span className="text-gray-500">{it.strategy} · {it.expiration} · {it.source}</span>
                      <span className="text-gray-400 text-xs ml-auto">{new Date(it.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {it.reasons.map((r) => (
                        <span key={r} className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">{r.replace('_', ' ')}</span>
                      ))}
                    </div>
                    {it.note && <div className="text-gray-600 mt-1">{it.note}</div>}
                    <div className="text-gray-500 mt-1 text-xs">
                      credit ${it.snapshot.credit} · RoR {(it.snapshot.returnOnRisk * 100).toFixed(1)}%
                      {it.comparison?.creditDiff != null && (
                        <> · vs {it.comparison.secondarySource}: credit diff {it.comparison.creditDiff > 0 ? '+' : ''}{it.comparison.creditDiff.toFixed(0)}</>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reset Trade Data */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-800">Reset Trade Data</CardTitle>
          <CardDescription>
            Delete all trade chains, positions, legs, and transactions. Schwab connection
            and account settings are preserved. Use this before a fresh full sync to rebuild
            all data from Schwab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {confirmReset ? (
              <>
                <Button variant="danger" onClick={handleResetTradeData}>
                  Yes, Delete All Trade Data
                </Button>
                <Button variant="secondary" onClick={() => setConfirmReset(false)}>
                  Cancel
                </Button>
                <span className="text-sm text-red-600">This cannot be undone.</span>
              </>
            ) : (
              <Button
                variant="danger"
                onClick={handleResetTradeData}
                disabled={resetResult.status === 'running'}
              >
                {resetResult.status === 'running' ? 'Deleting...' : 'Reset All Trade Data'}
              </Button>
            )}
            {resetResult.status === 'success' && (
              <span className="text-sm text-green-600">{resetResult.message}</span>
            )}
            {resetResult.status === 'error' && (
              <span className="text-sm text-red-600">{resetResult.message}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
